import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { declineHostOffer } from "../../../api/threads/threadHostingApi";
import { appPaths } from "../../../shared/routing/appPaths";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import { refusalText } from "../../../ui/feedback/refusalWords";
import StatusMessage from "../../../ui/feedback/StatusMessage";
import { useThreadContext } from "../page/threadPageContext";
import { actionDecision } from "../page/viewerActions";
import { takeOver, takeOverAutomatically } from "./automaticHosting";
import { hostOfferDecided } from "./incomingHostOffers";
import HostingChoiceForm from "./HostingChoiceForm";
import type { HostingChoice } from "./routeChoices";

interface HostOfferPanelProps {
  offerId: string;
  lastEntryNumber: number;
  /** Opened by the offer's link: deciding leaves its address. From the inbox, the decided offer leaves on its own. */
  leavesAddress: boolean;
}

/**
 * Taking over someone else's Thread (T-9; F55): the short confirmation that its agent then
 * runs on your PC and subscription, for everyone in it. Take over accepts the host's offer
 * on your last-used or only ready connector; when that needs your choice, the hosting form
 * asks. Opened from the hosting line, the inbox (Q-60) or the link the host sent.
 */
export default function HostOfferPanel({ offerId, lastEntryNumber, leavesAddress }: HostOfferPanelProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { threadId, overview, nameOf, currentUserId } = useThreadContext();
  const [choosing, setChoosing] = useState(false);
  const { state, submit } = useSubmission();
  const leave = () => {
    if (leavesAddress) void navigate(appPaths.thread(threadId), { replace: true });
  };

  if (overview.host === currentUserId) {
    return null;
  }
  // Module C's decision (W-22 slice 4): accepting runs it on your PC, which needs permission to propose changes, and
  // waits for an open turn; both Accepts say why.
  const accepting = actionDecision(overview.viewer, "accept_hosting");
  const acceptRefused = accepting.refusal === null ? undefined : refusalText(t, accepting.refusal);

  const takeOverNow = () => {
    submit(async () => {
      if (await takeOverAutomatically(offerId, overview, lastEntryNumber)) {
        leave();
      } else {
        setChoosing(true);
      }
    });
  };
  const takeOverWith = (choice: HostingChoice) => {
    submit(async () => {
      await takeOver(offerId, choice, lastEntryNumber);
      leave();
    });
  };

  return (
    <StatusMessage tone="info" title={t("threads.hostOffer.title", { host: nameOf(overview.host) })}>
      <div className="space-y-3">
        <p>{t("threads.hostOffer.explanation")}</p>
        {state.status === "failed" ? <ErrorNotice error={state.error} /> : null}
        {choosing ? (
          <HostingChoiceForm
            threadId={threadId}
            current={{ route: overview.settings.route, fallbackRoutes: overview.settings.fallback_routes ?? [], permissionProfile: overview.settings.permission_profile, connectorId: null }}
            submitLabel={t("threads.hostOffer.accept")}
            submitRefused={acceptRefused}
            submitting={state.status === "submitting"}
            onSubmit={takeOverWith}
            onCancel={() => { setChoosing(false); }}
          />
        ) : (
          <div className="flex gap-2">
            <AppButton variant="primary" size="sm" disabled={state.status === "submitting"} unavailable={acceptRefused} onClick={takeOverNow}>{t("threads.hostOffer.accept")}</AppButton>
            <AppButton
              variant="secondary"
              size="sm"
              disabled={state.status === "submitting"}
              onClick={() => { submit(async () => { await declineHostOffer(offerId); hostOfferDecided(offerId); leave(); }); }}
            >
              {t("threads.hostOffer.decline")}
            </AppButton>
          </div>
        )}
      </div>
    </StatusMessage>
  );
}
